// src/context/CreditsContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuthContext } from './AuthContext';
import { useAxiosAuth } from '../hooks/useAxiosAuth';

const CreditsContext = createContext({
  balance: null,
  used:    null,
  quota:   null,
  refreshCredits: () => {},
  startCheckout:  () => {},   // ← NEW
});

export const CreditsProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const axios    = useAxiosAuth();
  const [balance, setBalance] = useState(null);
  const [used,    setUsed]    = useState(null);
  const [quota,   setQuota]   = useState(null);

  const refreshCredits = async () => {
    if (!user) return;
    try {
      const { data } = await axios.get('/me/credits');
      setBalance(data.balance);
      setUsed(data.used_this_month);
      setQuota(data.monthly_quota);
    } catch (err) {
      console.error('Failed loading credits', err);
    }
  };

  // NEW ───────────────────────────────────────────────────────────────────────
  const startCheckout = async (packId) => {
    if (!user) throw new Error('Please sign in to buy credits.');

    try {
      const { data } = await axios.post('/billing/checkout-session', { packId });
      if (data?.url) {
        window.location.assign(data.url);
      } else {
        throw new Error('Unexpected response from the billing server.');
      }

    } catch (err) {
      let message;

      // 1) No response at all → network down / CORS / DNS / offline
      if (!err.response) {
        message = 'Network error: please check your internet connection and try again.';

      // 2) Server-side errors (502, 503, etc)
      } else if (err.response.status >= 500) {
        message = 'Our billing service is temporarily unavailable. Please try again in a few minutes.';

      // 3) Bad request or validation errors
      } else if (err.response.status >= 400 && err.response.status < 500) {
        // use whatever your API returned, or fallback
        message = err.response.data?.error
               || 'There was a problem with your request. Please reload and try again.';

      // 4) Everything else
      } else {
        message = 'An unexpected error occurred. Please try again.';
      }

      throw new Error(message);
    }
  };
  // ───────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    refreshCredits();
  }, [user]);

  return (
    <CreditsContext.Provider value={{ balance, used, quota, refreshCredits, startCheckout }}>
      {children}
    </CreditsContext.Provider>
  );
};

export const useCredits = () => useContext(CreditsContext);
